import { useTranslator } from '@translator-app/react';
import { DealerSelect } from '~/app/dealer/dealer-select';
import { ErrorFallback } from '~/app/error/error';
import { LandingPage } from '~/app/landing/landing-page';
import { Button } from '~/components/button';
import Heading from '~/components/heading';
import HideUnauthorized from '~/components/hide-unauthorized';
import PageSpinner from '~/components/page-spinner';
import Pagination from '~/components/pagination';
import { SelectionCard } from '~/components/selection-card';
import TextField from '~/components/textfield';
import { ApplicationContextProvider } from '~/contexts/application-context';
import { GraphQLProvider } from '~/contexts/graphql-context';
import { ServerNotificationsProvider } from '~/contexts/server-notifications-context';
import { UserProvider } from '~/contexts/user-context';
import { getAccessToken } from '~/utils/auth/client';
import { useRouter } from 'next/router';
import React from 'react';
import { useQuery } from 'urql';

import { AuthRolesEnum } from '../enums';
import { OrganisationObjectType, OrganisationPaginatedResultObjectType } from '../generated/graphql';
import { usePaginationQuery } from '../hooks/use-pagination';
import { AdminPageWrapper } from '../templates/admin';
import { queryParamToString } from '../utils/query';

const OrganisationFragment = `
fragment organisationFields on OrganisationObjectType {
  _id
  id
  name
  companyName
  vatNumber
  address {
    id
    street
    streetNumber
    city
    zipcode
    country
  }
}
`;

const OrganisationsQuery = `
  ${OrganisationFragment}

  query OrganisationsQuery($pagination: PaginationInputObjectType!, $dealerId: ObjectId) {
    organisations(pagination: $pagination, dealerId: $dealerId) {
      count
      total
      docs {
        ...organisationFields
      }
    }
  }
`;

const SearchOrganisationQuery = `
  ${OrganisationFragment}

  query SearchOrganisations($query: String!) {
    findOrganisations(query: $query) {
      ...organisationFields
    }
  }
`;

interface OrganisationsGridProps {
  organisations: Array<OrganisationObjectType>;
  isLoading: boolean;
  error: any;
}

const OrganisationsGrid: React.FC<OrganisationsGridProps> = (props) => {
  const router = useRouter();
  const { isLoading, error, organisations } = props;

  if (isLoading) {
    return <PageSpinner />;
  }

  if (error) {
    return <ErrorFallback />;
  }

  return (
    <div className="grid gap-4 grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 py-4">
      {organisations.map((organisation) => {
        return (
          <SelectionCard
            key={organisation._id}
            title={organisation.name}
            onPress={() => {
              router.push('/[organisationId]', `/${organisation._id}`);
            }}
            testId={`organisation-card-${organisation._id}`}
          >
            <div className="font-medium text-sm flex">{organisation.vatNumber}</div>
            <div className="text-gray-600 text-sm">
              <div>{organisation.companyName}</div>
              <div>
                {organisation.address?.street} {organisation.address?.streetNumber}
              </div>
              <div>
                {organisation.address?.zipcode} {organisation.address?.city}
              </div>
              <div>{organisation.address?.country}</div>
            </div>
          </SelectionCard>
        );
      })}
    </div>
  );
};

const OrganisationListPage: React.FC = () => {
  let router = useRouter();
  let { query } = router;
  let { translate } = useTranslator();
  let pagination = usePaginationQuery();
  let dealerId = queryParamToString(query.dealerId);
  let [listOrganisationsResult] = useQuery<{ organisations: OrganisationPaginatedResultObjectType }>({
    query: OrganisationsQuery,
    variables: {
      pagination,
      dealerId,
    },
  });
  let [searchQuery, setSearchQuery] = React.useState('');
  let [searchResults] = useQuery<{ findOrganisations: Array<OrganisationObjectType> }>({
    query: SearchOrganisationQuery,
    variables: {
      query: searchQuery,
    },
  });

  const { organisations, isLoading, error } = React.useMemo(() => {
    if (searchQuery) {
      const results = searchResults.data?.findOrganisations || [];
      return {
        organisations: results,
        isLoading: !!(searchResults.fetching && results.length),
        error: searchResults.error,
      };
    } else {
      const results = listOrganisationsResult.data?.organisations?.docs || [];
      return {
        organisations: results,
        isLoading: !!(listOrganisationsResult.fetching && results.length),
        error: listOrganisationsResult.error,
      };
    }
  }, [searchResults, listOrganisationsResult, searchQuery]);

  const totalOrganisations = listOrganisationsResult.data?.organisations?.total || 0;
  return (
    <div>
      <div className="flex flex-wrap justify-between">
        <Heading>{translate('organisationScreen.selectionHeading')}</Heading>
        <HideUnauthorized role={AuthRolesEnum.Dealer}>
          <div>
            <Button appearance="primary" href="/admin/organisations/create">
              {translate('organisationScreen.create')}
            </Button>
          </div>
        </HideUnauthorized>
      </div>

      <div className="flex flex-wrap">
        <div className="flex-grow">
          <TextField
            placeholder={translate('organisationScreen.search')}
            onChange={setSearchQuery}
            value={searchQuery}
          />
        </div>
        <div className="ml-4">
          <HideUnauthorized role={AuthRolesEnum.Dealer}>
            <DealerSelect
              value={dealerId}
              onSelect={(dealer) => {
                let queryClone = { ...query };
                if (!dealer) {
                  delete queryClone.dealerId;
                } else {
                  queryClone.dealerId = dealer.value;
                }
                router.push({ pathname: '/', query: queryClone });
              }}
            />
          </HideUnauthorized>
        </div>
      </div>

      <OrganisationsGrid organisations={organisations} isLoading={isLoading} error={error} />

      {!searchQuery && (
        <Pagination
          skip={pagination.skip}
          limit={pagination.limit}
          totalItems={totalOrganisations}
          onPageSelect={({ skip, limit }) => {
            router.push({ pathname: '/', query: { ...query, skip, limit } });
          }}
        />
      )}
    </div>
  );
};

const AuthenticatedHome: React.FC = () => {
  return (
    <ApplicationContextProvider>
      <GraphQLProvider>
        <UserProvider>
          <ServerNotificationsProvider>
            <AdminPageWrapper>
              <OrganisationListPage />
            </AdminPageWrapper>
          </ServerNotificationsProvider>
        </UserProvider>
      </GraphQLProvider>
    </ApplicationContextProvider>
  );
};

function Index() {
  const [authState, setAuthState] = React.useState<'loading' | 'guest' | 'authenticated'>('loading');

  React.useEffect(() => {
    getAccessToken({
      forceRefresh: true,
      shouldRedirect: false,
    })
      .then((result) => {
        setAuthState(result ? 'authenticated' : 'guest');
      })
      .catch(() => {
        setAuthState('guest');
      });
  }, []);

  if (authState === 'loading') {
    return <PageSpinner />;
  }

  if (authState === 'guest') {
    return <LandingPage />;
  }

  return <AuthenticatedHome />;
}

export default Index;
